// Pink Trombone engine driven by parameter trajectories (port of Samuel's usePinkTrombone, GPL-3.0).
const LEAD_S = 0.15, FADE_OUT_S = 0.05, SCRUB_TAU_S = 0.02, OPEN = 3, RESCHEDULE_LEAD_S = 0.05;
export const VELUM_OPEN = -1.7;

let loading = null;
export const loadPinkTrombone = () => {
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.type = 'module';
      s.src = '/lms/samuel/pink-trombone.min.js?v=2';
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('pink-trombone load failed'));
      document.head.appendChild(s);
    }).then(() => customElements.whenDefined('pink-trombone'));
  }
  return loading;
};

const hold = (p, t) => {
  const at = Math.max(0, t - 1e-5);
  if (p.cancelAndHoldAtTime) p.cancelAndHoldAtTime(at); else p.cancelScheduledValues(at);
};

export function createEngine() {
  const S = { el: null, tip: null, lip: null, vel: null, gate: null, dest: null, token: 0, pb: null, end: 0, voiceOn: true, rec: null };

  const params = () => [S.el.frequency, S.el.tenseness, S.el.loudness, S.el.tongue.index, S.el.tongue.diameter,
    S.tip.index, S.tip.diameter, S.lip.diameter, S.el.intensity, S.vel.diameter];

  const curves = (r) => {
    const p = r.params, n = p.voiceness.length;
    const v = Float32Array.from(p.voiceness, (x) => (S.voiceOn ? x : 0));
    return [Float32Array.from(p.frequency), v, v.map((x) => Math.pow(Math.max(x, 0), 0.25)),
      Float32Array.from(p.tongueIndex), Float32Array.from(p.tongueDiameter),
      Float32Array.from(p.constrictionIndex), Float32Array.from(p.constrictionDiameter),
      p.lipDiameter ? Float32Array.from(p.lipDiameter) : new Float32Array(n).fill(OPEN),
      Float32Array.from(p.intensity), p.velum ? Float32Array.from(p.velum) : new Float32Array(n).fill(OPEN)];
  };

  const curve = (param, values, t0, dur) => { param.setValueAtTime(values[0], t0 - 0.01); param.setValueCurveAtTime(values, t0, dur); };

  const init = async (el) => {
    if (S.el) return;
    await loadPinkTrombone();
    const ctx = new AudioContext({ latencyHint: 'playback' });
    await el.setAudioContext(ctx);
    el.vibrato.gain.value = 0; el.vibrato.wobble.value = 0; el.intensity.value = 0;
    S.tip = el.newConstriction(33, OPEN);
    S.lip = el.newConstriction(43, OPEN);
    S.vel = el.newConstriction(20, OPEN);
    const gate = ctx.createGain(); gate.gain.value = 0;
    const limiter = ctx.createDynamicsCompressor();
    Object.entries({ threshold: -6, knee: 6, ratio: 20, attack: 0.002, release: 0.12 }).forEach(([k, v]) => { limiter[k].value = v; });
    el.connect(gate); gate.connect(limiter); limiter.connect(ctx.destination);
    S.dest = ctx.createMediaStreamDestination(); limiter.connect(S.dest);
    S.gate = gate;
    el.start(); el.enableUI(); el.startUI();
    S.el = el;
    S.onBg = (e) => { if (e.detail || !S.el) return; stop(); S.el.audioContext.suspend().catch(() => {}); };
    window.addEventListener('vf-audio', S.onBg);
  };

  const ctx = () => S.el?.audioContext || null;
  const resume = () => S.el?.audioContext.resume();

  const speak = async (r, { speed = 1, startFrac = 0, onProgress } = {}) => {
    if (!S.el) return;
    const c = S.el.audioContext; await c.resume();
    const token = ++S.token, values = curves(r), n = r.params.voiceness.length, fr = r.frame_rate;
    const startFrame = Math.min(Math.max(0, Math.floor(startFrac * n)), n - 2);
    const duration = (n - startFrame) / fr / speed, now = c.currentTime;
    const t0 = Math.max(now + LEAD_S, S.end + 0.02);
    params().forEach((p, i) => { p.cancelScheduledValues(now); curve(p, values[i].subarray(startFrame), t0, duration); });
    const g = S.gate.gain;
    g.cancelScheduledValues(now); g.setValueAtTime(g.value, now); g.linearRampToValueAtTime(1, t0);
    g.setValueAtTime(1, t0 + duration); g.linearRampToValueAtTime(0, t0 + duration + FADE_OUT_S);
    const end = t0 + duration + FADE_OUT_S; S.end = end;
    const pb = { values, n, fr, startFrame, t0, duration, speed, end, wall: performance.now() + (end - now + 5) * 1000 };
    S.pb = pb;
    await new Promise((resolve) => {
      const poll = () => {
        if (S.token !== token) return resolve();
        const t = c.currentTime;
        onProgress?.(Math.min(1, (pb.startFrame + Math.max(0, t - pb.t0) * pb.speed * fr) / n));
        if (t >= pb.end + 0.05 || performance.now() > pb.wall) { onProgress?.(1); if (S.pb === pb) S.pb = null; resolve(); } else setTimeout(poll, 40);
      };
      poll();
    });
  };

  const setSpeed = (speed) => {
    const pb = S.pb; if (!pb || speed === pb.speed || !S.gate.gain.cancelAndHoldAtTime) return;
    const now = S.el.audioContext.currentTime;
    const frame = Math.floor(pb.startFrame + Math.max(0, now - pb.t0) * pb.speed * pb.fr);
    if (frame > pb.n - 3) return;
    const t0 = now + RESCHEDULE_LEAD_S, duration = (pb.n - frame) / pb.fr / speed;
    params().forEach((p, i) => { hold(p, now); curve(p, pb.values[i].subarray(frame), t0, duration); });
    const g = S.gate.gain, end = t0 + duration + FADE_OUT_S;
    hold(g, now); g.linearRampToValueAtTime(1, t0); g.setValueAtTime(1, t0 + duration); g.linearRampToValueAtTime(0, end);
    Object.assign(pb, { startFrame: frame, t0, duration, speed, end, wall: performance.now() + (end - now + 5) * 1000 });
    S.end = end;
  };

  const freeze = () => { S.token++; S.pb = null; const now = S.el.audioContext.currentTime; params().forEach((p) => hold(p, now)); hold(S.gate.gain, now); return now; };

  const stop = () => { if (!S.el) return; const now = freeze(); S.gate.gain.setTargetAtTime(0, now, SCRUB_TAU_S); S.end = now; };

  const scrub = (r, frac) => {
    if (!S.el) return;
    void S.el.audioContext.resume();
    const values = curves(r), n = r.params.voiceness.length;
    const f = Math.min(n - 1, Math.max(0, Math.round(frac * (n - 1))));
    const now = freeze();
    params().forEach((p, i) => p.setTargetAtTime(values[i][f], now, SCRUB_TAU_S));
    S.gate.gain.setTargetAtTime(1, now, 0.03); S.end = now;
  };

  // Hold one tract pose (VocalFitness posture / trapezoid / manual) with optional sound.
  const pose = (frame, sound) => {
    if (!S.el) return;
    const now = freeze();
    const idle = S.el.audioContext.state !== 'running';
    const set = (p, v) => { if (idle) { p.cancelScheduledValues(0); p.value = v; } else p.setTargetAtTime(v, now, 0.06); };
    set(S.el.tongue.index, frame.tongueIndex); set(S.el.tongue.diameter, frame.tongueDiameter);
    set(S.tip.index, frame.constrictionIndex); set(S.tip.diameter, frame.constrictionDiameter);
    set(S.lip.diameter, frame.lipDiameter); set(S.vel.diameter, frame.velum ?? OPEN);
    if (frame.frequency) set(S.el.frequency, frame.frequency);
    const v = S.voiceOn ? frame.voiceness : 0;
    set(S.el.tenseness, v); set(S.el.loudness, Math.pow(Math.max(v, 0), 0.25)); set(S.el.intensity, sound ? frame.intensity ?? 1 : 0);
    S.gate.gain.setTargetAtTime(sound ? 1 : 0, now, 0.03); S.end = now;
  };

  const voice = (frequency, voiceness) => {
    if (!S.el) return;
    void S.el.audioContext.resume();
    const now = freeze();
    S.el.frequency.setTargetAtTime(frequency, now, SCRUB_TAU_S);
    const v = S.voiceOn ? voiceness : 0;
    S.el.tenseness.setTargetAtTime(v, now, SCRUB_TAU_S);
    S.el.loudness.setTargetAtTime(Math.pow(Math.max(v, 1e-6), 0.25), now, SCRUB_TAU_S);
    S.el.intensity.setTargetAtTime(1, now, SCRUB_TAU_S);
    S.gate.gain.setTargetAtTime(1, now, 0.03); S.end = now;
  };

  const startVoice = () => {
    if (!S.el) return;
    voice(S.el.frequency.value || 120, S.el.tenseness.value || 0.6);
    const now = S.el.audioContext.currentTime;
    [S.tip, S.lip, S.vel].forEach((c) => c.diameter.setTargetAtTime(OPEN, now, SCRUB_TAU_S));
  };

  const fade = () => { if (!S.gate) return; const now = S.gate.context.currentTime; hold(S.gate.gain, now); S.gate.gain.setTargetAtTime(0, now, FADE_OUT_S); };

  const setWobble = (on) => { if (!S.el) return; const now = S.el.audioContext.currentTime; S.el.vibrato.wobble.setTargetAtTime(on ? 1 : 0, now, 0.05); S.el.vibrato.gain.setTargetAtTime(on ? 0.005 : 0, now, 0.05); };
  const setVoiceOn = (on) => { S.voiceOn = on; };

  const startRecording = () => {
    if (!S.el || S.rec) return;
    const canvases = Array.from(S.el.querySelectorAll('canvas')).sort((a, b) => Number(a.style.zIndex || 0) - Number(b.style.zIndex || 0));
    const audioStream = S.dest.stream;
    const out = document.createElement('canvas'); out.width = 600; out.height = 600;
    const g = out.getContext('2d'); let raf = 0;
    const draw = () => { g.fillStyle = '#fff'; g.fillRect(0, 0, 600, 600); canvases.forEach((c) => g.drawImage(c, 0, 0, 600, 600)); raf = requestAnimationFrame(draw); };
    draw();
    const mk = (stream) => { try { const r = new MediaRecorder(stream); const chunks = []; r.ondataavailable = (e) => e.data.size && chunks.push(e.data); r.start(); return { r, chunks }; } catch { return null; } };
    const video = out.captureStream ? mk(new MediaStream([...out.captureStream(30).getVideoTracks(), ...audioStream.getAudioTracks()])) : null;
    const audio = mk(audioStream);
    S.rec = { video, audio, stopDraw: () => cancelAnimationFrame(raf) };
  };

  const stopRecording = async () => {
    const rec = S.rec; if (!rec) return null; S.rec = null;
    const fin = (x, type) => (x ? new Promise((res) => { x.r.onstop = () => res(x.chunks.length ? new Blob(x.chunks, { type }) : null); x.r.stop(); }) : Promise.resolve(null));
    const [video, audio] = await Promise.all([fin(rec.video, 'video/webm'), fin(rec.audio, 'audio/webm')]);
    rec.stopDraw();
    return { video, audio };
  };

  const destroy = () => {
    if (!S.el) return;
    S.token++;
    window.removeEventListener('vf-audio', S.onBg);
    try { S.el.stop(); } catch { /* not started */ }
    const c = S.el.audioContext;
    if (c && c.state !== 'closed') c.close().catch(() => {});
    Object.assign(S, { el: null, tip: null, lip: null, vel: null, gate: null, dest: null, pb: null, end: 0 });
  };

  return { init, destroy, resume, speak, setSpeed, stop, scrub, pose, voice, startVoice, endVoice: fade, endScrub: fade, setWobble, setVoiceOn, startRecording, stopRecording, ctx, ready: () => !!S.el };
}
